import CustomLabel from "@/components/CustomLabel";
import { useAppSelector } from "@/store/hooks/hooks";
import { RootState } from "@/store/store";
import { isTouchScreen } from "@/utils/common";
import {
  ArrowUpFromLine,
  FileSpreadsheet,
  FileText,
  Trash2,
} from "lucide-react";
import { useRef, useState } from "react";

interface FileWithPreview extends File {
  preview?: string;
  id?: string;
  uniqueFile?: File; // Will hold the renamed File object
}

interface FileUploaderProps {
  required?: boolean;
  label?: string;
  onFilesChange: (files: FileWithPreview[]) => void;
  files: FileWithPreview[];
  maxFileSize?: number; // in MB
  acceptedFileTypes?: string[];
  error?: string | null;
  setError: (error: string | null) => void;
}

// Helper function to generate truly unique IDs
const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

// Helper function to create a new File object with a unique name
const createUniqueFile = (originalFile: File): File => {
  const fileExtension = originalFile.name.split(".").pop() || "";
  const uniqueName = `${
    originalFile.name.split(".")[0]
  }_${generateUniqueId()}.${fileExtension}`;

  // Create a new File object with the unique name
  return new File([originalFile], uniqueName, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });
};

function FileUploader({
  required = false,
  label = "Attach Images/Files",
  onFilesChange,
  files,
  maxFileSize = 5, // Default 5MB
  acceptedFileTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "application/pdf", // PDF
    "application/vnd.ms-excel", // Excel (older .xls format)
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    // Excel (newer .xlsx format)
  ],
  error,
  setError,
}: FileUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const settingsState = useAppSelector((state: RootState) => state.settings);

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check file type
    if (!acceptedFileTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type. Only ${acceptedFileTypes
          .map((type) => type.split("/")[1].toUpperCase())
          .join(", ")} are allowed.`,
      };
    }

    // Check file size
    const configFileSize =
      settingsState?.settings?.fileConfig?.fileSize || maxFileSize;
    if (file.size > configFileSize * 1024 * 1024) {
      return {
        valid: false,
        error: `File size exceeds ${configFileSize}MB limit.`,
      };
    }

    return { valid: true };
  };

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList) return;

    setError(null);

    const newFiles: FileWithPreview[] = [];
    const configMaxFiles = settingsState?.settings?.fileConfig?.maxFiles || 5;

    // Check if adding these files would exceed the maximum
    if (files.length + fileList.length > configMaxFiles) {
      setError(`You can only upload a maximum of ${configMaxFiles} files.`);
      // Reset input value to allow re-selecting the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Process each file
    Array.from(fileList).forEach((file) => {
      const validation = validateFile(file);

      if (!validation.valid) {
        setError(validation.error || "");
        return;
      }

      // Create a unique file with a unique name for the server
      const uniqueFile = createUniqueFile(file);

      const fileWithPreview = file as FileWithPreview;
      fileWithPreview.id = generateUniqueId();
      fileWithPreview.uniqueFile = uniqueFile;

      // Create preview for images only
      if (file.type.startsWith("image/")) {
        fileWithPreview.preview = URL.createObjectURL(file);
      }

      newFiles.push(fileWithPreview);
    });

    onFilesChange([...files, ...newFiles]);
    
    // Reset input value to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleRemoveFile = (fileId: string) => {
    const removedFile = files.find((file) => file.id === fileId);
    if (removedFile?.preview) {
      URL.revokeObjectURL(removedFile.preview);
    }

    const updatedFiles = files.filter((file) => file.id !== fileId);
    onFilesChange(updatedFiles);
  };

  // Get configured file size from settings or use default
  const configuredFileSize =
    settingsState?.settings?.fileConfig?.fileSize || maxFileSize;

  return (
    <div className="w-full">
      <CustomLabel label={label} required={required} />

      <div
        className={`w-full h-[100px] mb-4 p-2 flex flex-col items-center justify-center cursor-pointer border border-dashed ${
          isDragging
            ? "border-primary bg-blue-50"
            : "border-[#E1E1E1] bg-[#F6F8F9]"
        } transition-colors`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="w-[40px] h-[40px] rounded-[10px] bg-slate-100 flex items-center justify-center mb-3">
          <ArrowUpFromLine className="w-[20px] h-[20px] text-[#1F2336]" />
        </div>

        <p className="text-[#1F2336] text-[12px]">
          {!isTouchScreen() && <span>Drag and drop file here or </span>}click to
          <span className="text-primary font-bold"> browse</span>
        </p>

        <p className="text-[#9A9EAD] mt-2 text-[10px]">
          File formats:
          {acceptedFileTypes
            .slice(0, 4)
            .map((type) => type.split("/")[1].toUpperCase())
            .join(", ")}
          , EXCEL - Max. file size {configuredFileSize}MB
        </p>

        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept={acceptedFileTypes.join(",")}
          multiple
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

      {files.length > 0 && (
        <div className="mt-4">
          <div className="grid grid-cols-6 gap-2">
            {files.map((file) => {
              if (
                file.type === "application/pdf" ||
                file.type === "application/vnd.ms-excel" ||
                file.type ===
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              ) {
                return (
                  <div
                    key={file.id}
                    className="relative border rounded-md overflow-hidden group w-[142px] h-[128px] bg-gray-100 flex items-center justify-center"
                  >
                    <div className="absolute top-0 left-0 w-full h-[37%] bg-black bg-opacity-5 z-10"></div>
                    <div
                      className="absolute top-0 right-0 p-1 bg-white rounded-md opacity-100 cursor-pointer z-10 top-[12px] right-[12px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        file.id && handleRemoveFile(file.id);
                      }}
                    >
                      <Trash2 size={16} className="text-[#a40101]" />
                    </div>

                    {file.type === "application/pdf" ? (
                      <FileText size={48} className="text-primary-500" />
                    ) : (
                      <FileSpreadsheet size={48} className="text-primary-500" />
                    )}

                    <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-600 truncate px-2">
                      {file.name}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={file.id}
                  className="relative border rounded-md overflow-hidden group w-[142px] h-[128px]"
                >
                  <div className="absolute top-0 left-0 w-full h-[37%] bg-black bg-opacity-10 z-10"></div>
                  <div
                    className="absolute top-0 right-0 p-1 bg-white rounded-md opacity-100 cursor-pointer z-10 top-[12px] right-[12px]"
                    onClick={(e) => {
                      e.stopPropagation();
                      file.id && handleRemoveFile(file.id);
                    }}
                  >
                    <Trash2 size={16} className="text-[#a40101]" />
                  </div>
                  <img
                    src={file.preview}
                    alt={file.name}
                    className="object-cover h-full w-full"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default FileUploader;
